import { useGetMe } from 'api-hooks/auth/query';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { HotelCheckInStatusEnum } from 'common/constants/enum';
import { generateId } from 'common/utils/string';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  HotelSaleAddOnsItemFormType,
  HotelSaleFormType,
  HotelSaleGuestItemFormType,
  HotelSaleHotelItemFormType,
  HotelSaleHotelSalesItemFormType,
} from '../form-type';

interface Props {
  defaultValues?: HotelSaleFormType;
  hotelSale?: HotelSaleModel;
}

export function addNewAddOnsItem(): HotelSaleAddOnsItemFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
    priceUnit: undefined,
    unit: undefined,
  } as never as HotelSaleAddOnsItemFormType;
}

export function addNewGuestItem(isLead = false): HotelSaleGuestItemFormType {
  return {
    customId: generateId(),
    guestId: '',
    guestName: '',
    id: undefined,
    isLead,
    preferences: [],
    guestPreferences: [],
  } as never as HotelSaleGuestItemFormType;
}

export function addNewHotelItem(): HotelSaleHotelItemFormType {
  return {
    checkInStatus: HotelCheckInStatusEnum.NotCheckedIn,
    hotelConfirmationCode: '',
    checkIn: undefined,
    checkOut: undefined,
    customId: generateId(),
    detail: '',
    hotelRoomId: '',
    hotelRoomName: '',
    id: undefined,
    night: undefined,
    pax: undefined,
    price: undefined,
    room: undefined,
  } as never as HotelSaleHotelItemFormType;
}

export function addNewHotelSaleItem(): HotelSaleHotelSalesItemFormType {
  return {
    bookingCode: '',
    cityTax: '',
    customId: generateId(),
    defaultCheckInOut: {
      in: '',
      out: '',
    },
    hotelAddress: '',
    hotelEmail: '',
    hotelId: '',
    hotelName: '',
    hotelPhone: '',
    hotelSaleAddOns: [],
    hotelSaleGuests: [addNewGuestItem(true)],
    hotelSaleHotels: [addNewHotelItem()],
    id: undefined,
    isCityTax: false,
    isByPax: false,
    remark: '',
  } as never as HotelSaleHotelSalesItemFormType;
}

export default function useGetDefaultValues(props: Props) {
  const { hotelSale } = props;
  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;

  const defaultValues = React.useMemo<HotelSaleFormType>(() => {
    const dv = props.defaultValues;
    const transactionAt = hotelSale?.transactionAt ?? new Date();

    if (dv) {
      return {
        ...dv,
        transactionAt,
        tempTransactionAt: transactionAt,
        refId: undefined,
        hotelSale: undefined,
        hotelSales: dv.hotelSales.map((hs) => {
          return {
            ...hs,
            id: undefined,
            hotelSaleAddOns: hs.hotelSaleAddOns.map((hsao) => {
              return { ...hsao, id: undefined };
            }),
            hotelSaleGuests: hs.hotelSaleGuests.map((hsg) => {
              return { ...hsg, id: undefined };
            }),
            hotelSaleHotels: hs.hotelSaleHotels.map((hsh) => {
              return { ...hsh, id: undefined };
            }),
          };
        }),
      };
    }

    const customer = hotelSale?.customer;

    return {
      address: customer?.address || '',
      adminId: hotelSale?.admin?.id || employeeHook?.id || '',
      code: hotelSale?.code || '',
      customerBillingId: hotelSale?.customerBillingAddress?.id || '',
      customerBillingOptions: [],
      customerId: customer?.id || '',
      customerStatus: customer?.status || '',
      hasDueReceivable: customer?.hasDueReceivables || false,
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      description: hotelSale?.description || '',
      discount: hotelSale?.discount || 0,
      needApproval: false,
      transactionAt,
      tempTransactionAt: transactionAt,
      number: hotelSale?.invoiceNumber ?? '',
      operatorId: hotelSale?.operator?.id || '',
      otherCost: hotelSale?.otherCost || 0,
      phone: customer?.phone ?? '',
      refId: hotelSale?.refId,
      parentRefId: hotelSale?.parentSale?.refId,
      salesmanId: hotelSale?.salesman?.id ?? '',
      total: hotelSale?.total || 0,
      hotelSales: hotelSale?.hotelSales?.map((hs) => {
        const hotel = hs.hotel;

        const hotelSaleAddOns: HotelSaleAddOnsItemFormType[] =
          hs.hotelSaleAddOns.map((hsao) => {
            const guest = hsao.guest;
            return {
              customId: generateId(),
              description: hsao.description || '',
              guestId: guest.id || '',
              guestName: guestNameTransformer(guest),
              id: hsao.id,
              price: hsao.price || 0,
              priceUnit: hsao.priceUnit || 0,
              unit: hsao.unit || 0,
            };
          });

        const hotelSaleGuests: HotelSaleGuestItemFormType[] =
          hs.hotelSaleGuests.map((hsg) => {
            const guest = hsg.guest;
            return {
              customId: generateId(),
              guestId: guest.id || '',
              guestName: guestNameTransformer(guest),
              id: hsg.id,
              isLead: hsg.isLead,
              preferences: hsg.preferences.map((hsgp) => {
                return { id: hsgp.id, label: hsgp.name };
              }),
              guestPreferences: guestPreferencesTransformer(guest),
            };
          });

        const hotelSaleHotels: HotelSaleHotelItemFormType[] =
          hs.hotelSaleHotels.map((hsh) => {
            const room = hsh.hotelRoom;
            return {
              checkInStatus: hsh.checkInStatus || null,
              hotelConfirmationCode: hsh.hotelConfirmationCode || '',
              checkIn: hsh.checkIn,
              checkOut: hsh.checkOut,
              customId: generateId(),
              detail: hsh.detail || '',
              hotelRoomId: room.id || '',
              hotelRoomName: room.type,
              id: hsh.id,
              night: hsh.night,
              pax: hsh.pax || 0,
              price: hsh.price || 0,
              room: hsh.room,
            };
          });
        return {
          bookingCode: hs.bookingCode || '',
          cityTax: hs.cityTax || '',
          customId: generateId(),
          id: hs.id,
          hotelAddress: hotel.address || '',
          hotelEmail: hotel.email || '',
          hotelId: hotel.id || '',
          hotelName: hotel.name || '',
          hotelPhone: hotel.phone || '',
          isCityTax: hs.isCityTax || false,
          isByPax: hs.isByPax || false,
          remark: hs.remark || '',
          defaultCheckInOut: {
            in: '',
            out: '',
          },
          hotelSaleAddOns,
          hotelSaleGuests,
          hotelSaleHotels,
        };
      }) ?? [addNewHotelSaleItem()],
      hotelSale,
    };
  }, [employeeHook?.id, hotelSale, props.defaultValues]);

  return defaultValues;
}
